import { NgClass } from '@angular/common';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BreadcrumbsComponent } from 'components/breadcrumbs/breadcrumbs.component';
import { ContentSideNavComponent } from 'components/content-side-nav/content-side-nav.component';
import { ChangeReadingRoomModalComponent } from 'components/modal/change-reading-room-modal/change-reading-room-modal.component';
import { RequestAdditionalInfoModalComponent } from 'components/modal/request-additional-info-modal/request-additional-info-modal.component';
import { MultiOptionButtonComponent } from 'components/multi-option-button/multi-option-button.component';
import { ApplicationConversationComponent } from 'components/service/application-conversation/application-conversation.component';
import { ServiceSummaryComponent } from 'components/service/service-summary/service-summary.component';
import { SpinnerComponent } from 'components/spinner/spinner.component';
import { combineLatest, forkJoin } from 'rxjs';
import { ApiService } from 'services/api.service';
import { ToastService } from 'services/toast.service';
import { Breadcrumb } from 'types/breadcrumb.interface';
import {
  BackofficeReadingRoomSubmissionCard,
  ReadingRoomSubmissionCommunicationDto,
  ReadingRoomSubmissionCommunicationListItemDto,
  ReadingRoomSubmissionCommunicationListItemDtoPagedResult,
  ReadingRoomSubmissionNoteListByReadingRoomSubmissionIdItemDto,
  ReadingRoomSubmissionResearchTopicItem
} from 'types/service.interface';
import { BackofficeStatus, AttendenceStatus } from 'types/visit-room.interface';
import { formatDate } from 'util/date.utility';
import { ReassignUserModalComponent } from '../../components/modal/reassign-user-modal/reassign-user-modal.component';
import { ReadingRoomSubmissionVersionsComponent } from '../../components/service/reading-room-submission-versions/reading-room-submission-versions.component';
import { ApproveSubmissionModalComponent } from './modals/approve-submission-modal/approve-submission-modal.component';
import { CancelSubmissionModalComponent } from './modals/cancel-submission-modal/cancel-submission-modal.component';
import { RejectSubmissionModalComponent } from './modals/reject-submission-modal/reject-submission-modal.component';
import { ReadingRoomSubmissionNotesComponent } from 'components/service/reading-room-submission-notes/reading-room-submission-notes.component';

const KEY_ACTION_REDIRECT = 'pages.serviceProcessing.otherActionsOptions.changeReadingRoom';
const KEY_ACTION_REASSIGN = 'pages.serviceProcessing.otherActionsOptions.reassignToOtherUser';
const KEY_ACTION_REQUEST_ADDITIONAL_INFO = 'pages.serviceProcessing.otherActionsOptions.requestedAdditionalInformation';

const KEY_ACTION_CONFIRM = 'pages.serviceProcessing.submitOptions.confirm';
const KEY_ACTION_REJECT = 'pages.serviceProcessing.submitOptions.reject';
const KEY_ACTION_RECALL = 'pages.serviceProcessing.submitOptions.recall';

@Component({
  selector: 'app-service-processing-page',
  standalone: true,
  imports: [
    TranslateModule,
    SpinnerComponent,
    ContentSideNavComponent,
    BreadcrumbsComponent,
    ServiceSummaryComponent,
    NgClass,
    MultiOptionButtonComponent,
    ChangeReadingRoomModalComponent,
    ApplicationConversationComponent,
    ReassignUserModalComponent,
    RequestAdditionalInfoModalComponent,
    ApproveSubmissionModalComponent,
    CancelSubmissionModalComponent,
    RejectSubmissionModalComponent,
    ReadingRoomSubmissionVersionsComponent,
    ReadingRoomSubmissionNotesComponent
  ],
  templateUrl: './service-processing-page.component.html',
  styleUrls: ['./service-processing-page.component.scss', '../../components/form/base-form.scss']
})
export class ServiceProcessingPageComponent implements OnInit {
  public readonly attendenceStatuses = AttendenceStatus;
  public readonly formatDate = formatDate;
  @Input() isSubmittingNoteFiles: boolean = false;
  @ViewChild('rejectSubmissionModal') rejectSubmissionModal: any;
  @ViewChild('cancelSubmissionModal') cancelSubmissionModal: any;
  @ViewChild('approveSubmissionModal') approveSubmissionModal: any;
  breadcrumbs: Breadcrumb[] = [{ title: 'components.breadcrumbs.workspace', url: '/lasitavas-apmeklejums' }];
  applicationId: string = '';
  application?: BackofficeReadingRoomSubmissionCard;
  isLoading: boolean = true;
  isChangeReadingRoomDialogOpen: boolean = false;
  isReassignUserDialogOpen: boolean = false;
  isRejectSubmissionModalOpen = false;
  isCancelSubmissionModalOpen = false;
  hasUnreadMessages: boolean = false;
  messageCount: number = 0;
  unreadMessageCount: number = 0;
  conversationList?: ReadingRoomSubmissionCommunicationListItemDtoPagedResult;
  isRequestAdditionalInfoModalOpen: boolean = false;
  notes?: ReadingRoomSubmissionNoteListByReadingRoomSubmissionIdItemDto[];
  isSubmissionNotesLoading: boolean = true;
  notesCount: number = 0;
  navItemsCounts: { [key: string]: number } = {};

  readonly navItems: string[] = ['processApplication', 'processCommunication', 'processHistory', 'processNotes'];
  activeItem: string = this.navItems[0];

  filteredSubmitOptions: string[] = [KEY_ACTION_CONFIRM, KEY_ACTION_RECALL, KEY_ACTION_REJECT];

  submitOptions: string[] = [KEY_ACTION_CONFIRM, KEY_ACTION_RECALL, KEY_ACTION_REJECT];

  actions: string[] = [KEY_ACTION_REDIRECT, KEY_ACTION_REASSIGN, KEY_ACTION_REQUEST_ADDITIONAL_INFO];

  filteredActions: string[] = [KEY_ACTION_REDIRECT, KEY_ACTION_REASSIGN, KEY_ACTION_REQUEST_ADDITIONAL_INFO];

  get shouldRenderStartProcessingButton(): boolean {
    return (
      this.application?.backofficeStatus === BackofficeStatus.New || this.application?.backofficeStatus === BackofficeStatus.NewRedirected
    );
  }

  get shouldRenderSubmitButton() {
    return (
      this.application?.backofficeStatus === BackofficeStatus.InProcess ||
      this.application?.backofficeStatus === BackofficeStatus.RequestedAdditionalInformation ||
      this.application?.backofficeStatus == BackofficeStatus.SubmittedAdditionalInformation ||
      this.application?.backofficeStatus == BackofficeStatus.Approved
    );
  }

  get shouldRenderAttendenceButton() {
    const visitDate = new Date(this.application?.visitDate ?? '');
    const today = new Date();
    visitDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return visitDate <= today;
  }

  get shouldRenderAttendencyButtons(): boolean {
    return this.application?.backofficeStatus === BackofficeStatus.Approved;
  }

  get shouldRenderOherActionsButton(): boolean {
    const status = this.application?.backofficeStatus;

    return status !== BackofficeStatus.Rejected && status !== BackofficeStatus.Cancelled;
  }

  constructor(
    private apiService: ApiService,
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    window.scrollTo(0, 0);

    combineLatest([this.route.params, this.route.queryParams]).subscribe(([params]) => {
      if (params['id'] && !this.application) {
        this.applicationId = params['id'];

        const applicationObservable = this.apiService.getReadingRoomSubmission(params['id']);
        const conversationObservable = this.apiService.getSubmissionCommunicationList(params['id']);
        const notesObservable = this.apiService.getReadingRoomSubmissionNotesByIdList(params['id']);

        forkJoin([applicationObservable, conversationObservable, notesObservable]).subscribe(
          ([application, conversation, notesObservable]) => {
            this.application = application;
            this.conversationList = conversation;
            this.notes = notesObservable;
            this.messageCount = this.conversationList?.totalCount ?? 0;
            this.unreadMessageCount =
              this.conversationList?.list.filter((message) => !message.isReadByBackoffice && message.frontofficeText).length ?? 0;
            this.notesCount = this.notes.length;
            this.hasUnreadMessages = this.unreadMessageCount > 0;

            this.navItemsCounts = {
              processCommunication: this.messageCount,
              processNotes: this.notesCount
            };

            this.updateOptions();
            this.updateSubmitOptions();
            this.isLoading = false;
            this.breadcrumbs.push({
              title: `${this.translateService.instant('components.breadcrumbs.serviceProcessing')} ${this.application?.code ?? '-'}`,
              url: `/pieteikums/${this.applicationId}`
            });
          }
        );
      }
    });
  }

  onConversationUpdated(data: boolean): void {
    this.hasUnreadMessages = data;
  }

  startProcessing(): void {
    if (this.applicationId) {
      this.apiService.getReadingRoomSubmission(this.applicationId).subscribe((data: BackofficeReadingRoomSubmissionCard) => {
        this.application = data;

        if (this.canProcessSubmission()) {
          this.apiService.startProcessing(this.applicationId).subscribe((data: BackofficeReadingRoomSubmissionCard) => {
            this.application = data;
            this.toastService.showSuccess('startProcessingSuccess', '', {
              shouldTranslate: true
            });
            this.updateOptions();
            this.updateSubmitOptions();
          });
        } else {
          this.toastService.showDanger('statusChangedError', '', {
            shouldTranslate: true
          });
        }
      });
    }
  }

  rejectSubmission(rejectionReason: string | void): void {
    if (this.applicationId) {
      this.apiService.getReadingRoomSubmission(this.applicationId).subscribe((data: BackofficeReadingRoomSubmissionCard) => {
        this.application = data;

        if (this.canRejectSubmission()) {
          this.apiService.rejectSubmission(this.applicationId, rejectionReason!).subscribe((data: BackofficeReadingRoomSubmissionCard) => {
            this.application = data;
            this.toastService.showSuccess('rejectReadingRommSubmission', '', {
              shouldTranslate: true
            });
            this.updateOptions();
            this.updateSubmitOptions();
            this.isRejectSubmissionModalOpen = false;
          });
        } else {
          this.toastService.showDanger('statusChangedError', '', {
            shouldTranslate: true
          });
        }
      });
    }
  }

  cancelSubmission(cancellationReason: string | void): void {
    if (this.applicationId) {
      this.apiService.getReadingRoomSubmission(this.applicationId).subscribe((data: BackofficeReadingRoomSubmissionCard) => {
        this.application = data;

        if (this.canCancelSubmission()) {
          this.apiService
            .cancelSubmission(this.applicationId, cancellationReason!)
            .subscribe((data: BackofficeReadingRoomSubmissionCard) => {
              this.application = data;
              this.toastService.showSuccess('cancelReadingRommSubmission', '', {
                shouldTranslate: true
              });
              this.updateOptions();
              this.updateSubmitOptions();
              this.isCancelSubmissionModalOpen = false;
            });
        } else {
          this.toastService.showDanger('cancelSubmissionValidationError', '', {
            shouldTranslate: true
          });
        }
      });
    }
  }

  approveSubmission(informationForClient: string | void): void {
    if (this.applicationId) {
      this.apiService.getReadingRoomSubmission(this.applicationId).subscribe((data: BackofficeReadingRoomSubmissionCard) => {
        this.application = data;

        if (this.canApproveSubmission()) {
          this.apiService.approveSubmission(this.applicationId, informationForClient!).subscribe({
            next: (data: BackofficeReadingRoomSubmissionCard) => {
              this.application = data;
              this.toastService.showSuccess('approveReadingRommSubmission', '', {
                shouldTranslate: true
              });
              this.updateOptions();
              this.updateSubmitOptions();
            },
            error: (error: any) => {
              console.error(error);

              const fileCardError = error?.error?.errors?.['FileCard']?.[0];
              if (fileCardError === 'Can not add file with FileTermType Unknown') {
                this.toastService.showDanger('fileConfirmValidationError', '', {
                  shouldTranslate: true
                });
              } else {
                this.toastService.showFormValidationError();
              }
            }
          });
        } else {
          this.toastService.showDanger('statusChangedError', '', {
            shouldTranslate: true
          });
        }
      });
    }
  }

  setAttended() {
    if (this.applicationId) {
      this.apiService
        .attendStatusReadingRoomSumbission({
          readingRoomSubmissionId: this.applicationId.toString(),
          attendanceStatus: 'Attended'
        })
        .subscribe((data: BackofficeReadingRoomSubmissionCard) => {
          this.application = data;
          this.toastService.showSuccess('attendenceStatusChange', '', {
            shouldTranslate: true
          });
          this.updateSubmitOptions();
        });
      // TODO
      this.updateOptions();
    }
  }

  setUnattended() {
    if (this.applicationId) {
      this.apiService
        .attendStatusReadingRoomSumbission({
          readingRoomSubmissionId: this.applicationId.toString(),
          attendanceStatus: 'NotAttended'
        })
        .subscribe((data: BackofficeReadingRoomSubmissionCard) => {
          this.application = data;
          this.toastService.showSuccess('attendenceStatusChange', '', {
            shouldTranslate: true
          });
          this.updateSubmitOptions();
        });
      this.updateOptions();
    }
  }

  hasExpiredFiles(researchTopic: ReadingRoomSubmissionResearchTopicItem): boolean {
    const today = new Date();

    const allFiles = [...(researchTopic.backofficeTopicFiles || []), ...(researchTopic.backofficeJustificationFiles || [])];

    return allFiles.some((file) => {
      const note = file.fileNote;
      if (note?.termType === 'Fixed' && note.validTill) {
        const validTillDate = new Date(note.validTill);
        return validTillDate < today;
      } else if (note?.termType === 'Unknown') {
        return true;
      }
      return false;
    });
  }

  onSubmitOptionSelected(option: string): void {
    const hasExpired = this.application?.researchTopics?.some((topic) => this.hasExpiredFiles(topic));

    switch (option) {
      case KEY_ACTION_CONFIRM:
        // check if application has invalid file terms
        if (hasExpired) {
          this.toastService.showDanger('fileConfirmValidationError', '', {
            shouldTranslate: true
          });
          return;
        }
        this.approveSubmissionModal.show();
        break;
      case KEY_ACTION_RECALL:
        this.isCancelSubmissionModalOpen = true;
        break;
      case KEY_ACTION_REJECT:
        this.isRejectSubmissionModalOpen = true;
        break;
    }
  }

  onOtherActionsOptionSelected(option: string): void {
    switch (option) {
      case KEY_ACTION_REDIRECT:
        this.openChangeReadingRoomDialog();
        break;
      case KEY_ACTION_REASSIGN:
        this.openReassignUserDialog();
        break;
      case KEY_ACTION_REQUEST_ADDITIONAL_INFO:
        this.isRequestAdditionalInfoModalOpen = true;
        break;
    }
  }

  openChangeReadingRoomDialog(): void {
    this.isChangeReadingRoomDialogOpen = true;
  }

  openReassignUserDialog(): void {
    this.isReassignUserDialogOpen = true;
  }

  closeChangeReadingRoomDialog(): void {
    this.isChangeReadingRoomDialogOpen = false;
  }

  closeReassignUserDialog(): void {
    this.isReassignUserDialogOpen = false;
  }

  onChangeReadingRoom(application: BackofficeReadingRoomSubmissionCard): void {
    this.application = application;
    this.updateOptions();
    this.updateSubmitOptions();
  }

  updateSubmitOptions() {
    this.filteredSubmitOptions = this.submitOptions;
    const status = this.application?.backofficeStatus;
    const actionsToRemove: string[] = [];

    // apstiprinātam
    if (status && [BackofficeStatus.Approved].includes(status as BackofficeStatus)) {
      actionsToRemove.push(KEY_ACTION_CONFIRM, KEY_ACTION_REJECT);
    }

    // apstrādē
    if (status && [BackofficeStatus.InProcess].includes(status as BackofficeStatus)) {
      this.filteredSubmitOptions = this.filteredSubmitOptions = [KEY_ACTION_CONFIRM, KEY_ACTION_RECALL, KEY_ACTION_REJECT];
    }

    if (
      status &&
      [
        BackofficeStatus.New,
        BackofficeStatus.NewRedirected,
        BackofficeStatus.Rejected,
        BackofficeStatus.Unknown,
        BackofficeStatus.Cancelled
      ].includes(status as BackofficeStatus)
    ) {
      actionsToRemove.push(KEY_ACTION_CONFIRM, KEY_ACTION_RECALL, KEY_ACTION_REJECT);
    }

    this.filteredSubmitOptions = this.filteredSubmitOptions.filter((action) => !actionsToRemove.includes(action));
  }

  updateOptions() {
    this.filteredActions = this.actions;
    const status = this.application?.backofficeStatus;
    // redirect to other reading room, reassign to othe user, request additional information

    // apstiprinātam
    if (status && [BackofficeStatus.Approved].includes(status as BackofficeStatus)) {
      this.filteredActions = this.filteredActions = [KEY_ACTION_REQUEST_ADDITIONAL_INFO];
    }

    // apstrādē
    if (status && [BackofficeStatus.InProcess].includes(status as BackofficeStatus)) {
      this.filteredActions = this.filteredActions = [KEY_ACTION_REQUEST_ADDITIONAL_INFO, KEY_ACTION_REDIRECT, KEY_ACTION_REASSIGN];
    }

    if (
      status &&
      [BackofficeStatus.Rejected, BackofficeStatus.Draft, BackofficeStatus.Unknown, BackofficeStatus.Cancelled].includes(
        status as BackofficeStatus
      )
    ) {
      this.filteredActions = this.filteredActions.filter((action) => action !== KEY_ACTION_REDIRECT);
      this.filteredActions = this.filteredActions.filter((action) => action !== KEY_ACTION_REASSIGN);
    } else if (
      status &&
      [
        BackofficeStatus.New,
        BackofficeStatus.Rejected,
        BackofficeStatus.Cancelled,
        BackofficeStatus.NewRedirected,
        BackofficeStatus.Unknown
      ].includes(status as BackofficeStatus)
    ) {
      this.filteredActions = this.filteredActions.filter((action) => action !== KEY_ACTION_REQUEST_ADDITIONAL_INFO);
    } else if (status && [BackofficeStatus.NewRedirected].includes(status as BackofficeStatus)) {
      this.filteredActions = this.filteredActions = [KEY_ACTION_REDIRECT, KEY_ACTION_REASSIGN, KEY_ACTION_REQUEST_ADDITIONAL_INFO];
    }
  }

  onNoteChanged(data: any) {
    this.isLoading = true;
    this.apiService.getReadingRoomSubmissionNotesByIdList(this.application?.id ?? '').subscribe((notes) => {
      this.notes = notes;
      this.notesCount = notes.length ?? 0;
      this.navItemsCounts = {
        processCommunication: this.messageCount,
        processNotes: this.notesCount
      };

      this.isLoading = false;
    });
  }

  onScheduleUpdated(applicationData: BackofficeReadingRoomSubmissionCard) {
    this.apiService.getReadingRoomSubmission(applicationData?.id ?? '').subscribe((data) => {
      this.application = data;
      this.isLoading = false;
      this.updateOptions();
      this.updateSubmitOptions();
    });
  }

  onMessageSaved(message: ReadingRoomSubmissionCommunicationListItemDto | ReadingRoomSubmissionCommunicationDto) {
    this.isLoading = true;

    forkJoin([
      this.apiService.getSubmissionCommunicationList(this.application?.id ?? ''),
      this.apiService.getReadingRoomSubmission(this.application?.id ?? '')
    ]).subscribe(([conversation, application]) => {
      this.conversationList = conversation;
      this.application = application;
      this.updateOptions();
      this.updateSubmitOptions();
      this.messageCount = this.conversationList?.list.length ?? 0;
      this.unreadMessageCount =
        this.conversationList?.list.filter((message) => !message.isReadByBackoffice && message.frontofficeText).length ?? 0;
      this.hasUnreadMessages = this.unreadMessageCount > 0;

      this.navItemsCounts = {
        processCommunication: this.messageCount,
        processNotes: this.notesCount
      };

      this.isLoading = false;
    });
  }

  onRequestAdditionalInfo(message: ReadingRoomSubmissionCommunicationDto): void {
    this.isLoading = true;

    if (this.application) {
      this.application.backofficeStatus =
        (message as ReadingRoomSubmissionCommunicationDto).backofficeStatus == BackofficeStatus.RequestedAdditionalInformation
          ? BackofficeStatus.RequestedAdditionalInformation
          : this.application.backofficeStatus;
    }

    this.apiService.getSubmissionCommunicationList(this.application?.id ?? '').subscribe((conversation) => {
      this.conversationList = conversation;
      this.messageCount = this.conversationList?.list.length ?? 0;
      this.unreadMessageCount =
        this.conversationList?.list.filter((message) => !message.isReadByBackoffice && message.frontofficeText).length ?? 0;
      this.hasUnreadMessages = this.unreadMessageCount > 0;
      this.isLoading = false;
      this.updateOptions();
      this.updateSubmitOptions();
    });
  }

  checkUnreadMessages(): void {
    this.unreadMessageCount = this.unreadMessageCount - 1;
    this.hasUnreadMessages = this.unreadMessageCount > 0;
  }

  getSubmissionAllNotesBySubmissionId(): void {
    this.apiService.getReadingRoomSubmissionNotesByIdList(this.application?.id ?? '').subscribe(
      (data: ReadingRoomSubmissionNoteListByReadingRoomSubmissionIdItemDto[]) => {
        this.isSubmissionNotesLoading = false;
        this.notes = data;
        this.notesCount = data?.length ?? 0;
      },
      (error) => {
        console.error(error);
      }
    );
  }

  canProcessSubmission(): boolean {
    const status = this.application?.backofficeStatus;
    return (
      !!status && ![BackofficeStatus.Approved, BackofficeStatus.Rejected, BackofficeStatus.Cancelled].includes(status as BackofficeStatus)
    );
  }

  canRejectSubmission(): boolean {
    const status = this.application?.backofficeStatus;
    return (
      !!status && ![BackofficeStatus.Rejected, BackofficeStatus.Cancelled, BackofficeStatus.Approved].includes(status as BackofficeStatus)
    );
  }

  canCancelSubmission(): boolean {
    const status = this.application?.backofficeStatus;
    return (
      !!status && ![BackofficeStatus.Rejected, BackofficeStatus.Cancelled].includes(status as BackofficeStatus)
    );
  }

  canApproveSubmission(): boolean {
    const status = this.application?.backofficeStatus;
    return (
      !!status && ![BackofficeStatus.Approved, BackofficeStatus.Rejected, BackofficeStatus.Cancelled].includes(status as BackofficeStatus)
    );
  }

  canRequestAdditionalInfo(): boolean {
    const status = this.application?.backofficeStatus;
    return (
      !!status && ![BackofficeStatus.Approved, BackofficeStatus.Rejected, BackofficeStatus.Cancelled].includes(status as BackofficeStatus)
    );
  }
}
